import { ethers } from "ethers";
import { api } from "./api";

export function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}

export function isEvmAddress(value) {
    return typeof value === "string" && /^0x[a-fA-F0-9]{40}$/.test(value.trim());
}

export function isIntegerString(value) {
    if (!isNonEmptyString(value)) return false;
    return /^[0-9]+$/.test(value.trim());
}

export function isHexBytes(value) {
    if (!isNonEmptyString(value)) return false;
    const clean = value.trim();
    return /^0x([a-fA-F0-9]{2})*$/.test(clean);
}

export function normalizeSourceMode(value) {
    return String(value || "").trim().toLowerCase();
}

export function getTokenInfoSourceMode(tokenInfo) {
    return normalizeSourceMode(
        tokenInfo?.sourceMode ||
        tokenInfo?.deployment?.sourceMode ||
        tokenInfo?.deployment?.token?.sourceMode ||
        "standard",
    );
}

export function isCustomTokenInfo(tokenInfo) {
    return getTokenInfoSourceMode(tokenInfo) === "custom";
}

export function getCustomActionCapabilities(tokenInfo) {
    const caps =
        tokenInfo?.actionCapabilities ||
        tokenInfo?.deployment?.actionCapabilities ||
        tokenInfo?.deployment?.token?.actionCapabilities ||
        {};

    return caps && typeof caps === "object" ? caps : {};
}

export function buildCustomTokenInfoFromOverview({ overview, deployment, token }) {
    const mergedDeployment = {
        ...(deployment || {}),
        ...(overview?.deployment || {}),
    };

    const mergedToken = {
        ...(token || {}),
        ...(overview?.token || {}),
    };

    return {
        ...(overview || {}),
        ...(mergedToken || {}),

        sourceMode:
            overview?.sourceMode ||
            mergedDeployment?.sourceMode ||
            mergedToken?.sourceMode ||
            "standard",

        deploymentId:
            overview?.deploymentId ||
            mergedDeployment?.deploymentId ||
            mergedDeployment?.id ||
            null,

        requestId:
            overview?.requestId ||
            mergedDeployment?.requestId ||
            null,

        customVersionId:
            overview?.customVersionId ||
            mergedDeployment?.customVersionId ||
            null,

        actionCapabilities:
            overview?.actionCapabilities ||
            mergedDeployment?.actionCapabilities ||
            mergedToken?.actionCapabilities ||
            {},

        capabilities:
            overview?.capabilities ||
            mergedDeployment?.capabilities ||
            mergedToken?.capabilities ||
            {},

        deployment: mergedDeployment,
        token: mergedToken,
    };
}

export function getTokenInfoDeploymentId(tokenInfo) {
    return (
        tokenInfo?.deploymentId ||
        tokenInfo?.deployment?.deploymentId ||
        tokenInfo?.deployment?.id ||
        null
    );
}

export function getTokenInfoRequestId(tokenInfo) {
    return tokenInfo?.requestId || tokenInfo?.deployment?.requestId || null;
}

export function getSafeDecimals(value, fallback = 18) {
    const n = Number(value);

    if (Number.isInteger(n) && n >= 0 && n <= 36) {
        return n;
    }

    return fallback;
}

export function buildCustomMintArgs({
    tokenType,
    to,
    tokenId,
    amount,
    data,
    tokenInfo,
}) {
    const standard = String(tokenType || "").trim().toUpperCase();
    const cleanTo = String(to || "").trim();

    if (!isEvmAddress(cleanTo)) {
        throw new Error("Mint recipient must be a valid EVM address.");
    }

    if (standard === "ERC20") {
        const decimals = getSafeDecimals(
            tokenInfo?.decimals ??
            tokenInfo?.deployment?.decimals ??
            tokenInfo?.deployment?.token?.decimals ??
            18,
            18,
        );

        const humanAmount = String(amount || "").trim();

        if (!isNonEmptyString(humanAmount)) {
            throw new Error("Amount is required.");
        }

        let rawAmount;

        try {
            rawAmount = ethers.parseUnits(humanAmount, decimals).toString();
        } catch {
            throw new Error("Enter a valid ERC20 amount.");
        }

        if (rawAmount === "0") {
            throw new Error("Amount must be greater than zero.");
        }

        return {
            standard: "ERC20",
            to: cleanTo,
            amount: rawAmount,
            amountHuman: humanAmount,
            decimals,
        };
    }

    if (standard === "ERC721") {
        const cleanTokenId = String(tokenId || "").trim();

        if (!isIntegerString(cleanTokenId)) {
            throw new Error("Token ID must be an integer.");
        }

        return {
            standard: "ERC721",
            to: cleanTo,
            tokenId: cleanTokenId,
        };
    }

    if (standard === "ERC1155") {
        const cleanTokenId = String(tokenId || "").trim();
        const cleanAmount = String(amount || "").trim();
        const cleanData = String(data || "0x").trim() || "0x";

        if (!isIntegerString(cleanTokenId)) {
            throw new Error("ERC1155 Token ID must be an integer.");
        }

        if (!isIntegerString(cleanAmount) || cleanAmount === "0") {
            throw new Error("ERC1155 amount must be a positive integer.");
        }

        if (!isHexBytes(cleanData)) {
            throw new Error("ERC1155 data must be 0x hex bytes.");
        }

        return {
            standard: "ERC1155",
            to: cleanTo,
            tokenId: cleanTokenId,
            amount: cleanAmount,
            data: cleanData,
        };
    }

    throw new Error("Unsupported token type for custom mint.");
}

export function buildCustomBurnArgs({
    tokenType,
    from,
    tokenId,
    amount,
    tokenInfo,
}) {
    const standard = String(tokenType || "").trim().toUpperCase();
    const cleanFrom = String(from || "").trim();

    if (!isEvmAddress(cleanFrom)) {
        throw new Error("Connected wallet must be a valid EVM address.");
    }

    if (standard === "ERC20") {
        const decimals = getSafeDecimals(
            tokenInfo?.decimals ??
            tokenInfo?.deployment?.decimals ??
            tokenInfo?.deployment?.token?.decimals ??
            18,
            18,
        );

        const humanAmount = String(amount || "").trim();

        if (!isNonEmptyString(humanAmount)) {
            throw new Error("Amount is required.");
        }

        let rawAmount;

        try {
            rawAmount = ethers.parseUnits(humanAmount, decimals).toString();
        } catch {
            throw new Error("Enter a valid ERC20 amount.");
        }

        if (rawAmount === "0") {
            throw new Error("Amount must be greater than zero.");
        }

        return {
            standard: "ERC20",
            from: cleanFrom,
            amount: rawAmount,
            amountHuman: humanAmount,
            decimals,
        };
    }

    if (standard === "ERC721") {
        const cleanTokenId = String(tokenId || "").trim();

        if (!isIntegerString(cleanTokenId)) {
            throw new Error("Token ID must be an integer.");
        }

        return {
            standard: "ERC721",
            from: cleanFrom,
            tokenId: cleanTokenId,
        };
    }

    if (standard === "ERC1155") {
        const cleanTokenId = String(tokenId || "").trim();
        const cleanAmount = String(amount || "").trim();

        if (!isIntegerString(cleanTokenId)) {
            throw new Error("ERC1155 Token ID must be an integer.");
        }

        if (!isIntegerString(cleanAmount) || cleanAmount === "0") {
            throw new Error("ERC1155 amount must be a positive integer.");
        }

        return {
            standard: "ERC1155",
            from: cleanFrom,
            tokenId: cleanTokenId,
            amount: cleanAmount,
        };
    }

    throw new Error("Unsupported token type for custom burn.");
}

export async function prepareCustomTokenAction({
    tokenInfo,
    network,
    contractAddress,
    action,
    args,
}) {
    const cleanNetwork = String(network || "").trim().toLowerCase();
    const cleanContractAddress = String(contractAddress || "").trim();

    if (!cleanNetwork) {
        throw new Error("Network is required.");
    }

    if (!isEvmAddress(cleanContractAddress)) {
        throw new Error("Contract address must be a valid EVM address.");
    }

    const res = await api.post("/custom-contracts/actions/prepare", {
        deploymentId: getTokenInfoDeploymentId(tokenInfo),
        requestId: getTokenInfoRequestId(tokenInfo),
        network: cleanNetwork,
        contractAddress: cleanContractAddress,
        action,
        args: args || {},
    });

    const data = res.data;

    if (!data?.ok || !data?.tx?.data || !data?.actionRequestId) {
        throw new Error(
            data?.error ||
            data?.message ||
            "Backend did not return a valid custom action transaction.",
        );
    }

    return data;
}

export async function confirmCustomTokenAction({
    actionRequestId,
    txHash,
    confirmations,
}) {
    const res = await api.post("/custom-contracts/actions/confirm", {
        actionRequestId,
        txHash,
        confirmations,
    });

    return res.data;
}